/**
 * Inspector -- right-side panel surfaced when a constellation node
 * is selected. Bespoke (NOT EntityDetailLayout) because the panel
 * is dense + SVG-adjacent + carries the state-pill trace
 * affordance baked in.
 */
import { useTranslation } from "react-i18next";

import { useTwinBlast } from "twin/api/constellation_hooks";
import { KIND_LABEL } from "twin/constellation/TwinViz";
import { STATE_DOT, type NodeVisualState } from "twin/constellation/nodeState";
import { SEAL_DOT, sealWord } from "twin/constellation/sealWord";
import { formatSARCompact } from "shared/money/money";
import type { TwinEdge, TwinNode, TwinNodeKind } from "shared/api/types";

export interface InspectorProps {
  node: TwinNode;
  nodes: TwinNode[];
  edges: TwinEdge[];
  state: NodeVisualState;
  onClose: () => void;
  onSelect: (id: string) => void;
  onTraceState: () => void;
  onTraceBlast: () => void;
}

const STATE_LABEL: Record<NodeVisualState, string> = {
  down: "Down",
  degraded: "Degraded",
  "at-risk": "At risk",
  stable: "Stable",
};

/**
 * The inspector's own width in CSS pixels, matching its `w-72` class.
 *
 * Round 12 E4: the scene reserves this corner so no node is ever drawn
 * under the panel. The reserve has to be in place before the first
 * selection -- otherwise the first click is the one that moves the
 * scene -- so it is taken from the declared width rather than measured
 * off an element that does not exist until something is selected. A
 * test holds the two in step.
 */
export const INSPECTOR_WIDTH_PX = 288;

/**
 * The tallest the inspector may grow, as a fraction of the STAGE.
 *
 * Round 12 E4, and the reason it is a fraction of the stage rather than
 * of the viewport. It was `max-h-[60vh]`, which on a 900px window is
 * 540px over a 600px stage -- nine tenths of the picture -- so the
 * panel's footprint depended on the browser window and on how many
 * dependencies the selected node happened to have. Measured, a click at
 * one business service's own centre landed on the inspector's HEADING,
 * because the panel opened tall enough to reach a band the layout had
 * not reserved.
 *
 * At a half of the stage the panel's bottom-anchored footprint covers
 * exactly the two lowest tier bands, which is what `observatoryLayout`
 * reserves the end of. The two numbers are a pair and a test holds them
 * so: the panel may not grow into a band the layout still draws in. It
 * scrolls internally, so a half is a scroll rather than a loss.
 */
export const INSPECTOR_MAX_STAGE_FRACTION = 0.5;

export function Inspector({
  node,
  nodes,
  edges,
  state,
  onClose,
  onSelect,
  onTraceState,
  onTraceBlast,
}: InspectorProps): JSX.Element {
  const { t } = useTranslation();
  const byId = useMemoMap(nodes);
  /* Round 18 B16 -- "Stable" is a verdict. On estate D it was printed,
     green, on 199 of 400 nodes nothing had judged, under the view's
     own "This is the absence of a verdict, not a clean one". A finding
     still shows as one; only the no-finding case of an unjudged node
     changes. `judged` absent (an older reply) reads as before. */
  const unassessed = state === "stable" && node.judged === false;
  /* Round 19 B25 -- with the seal in the reply, the word is the seal's
     (one rule with the hover and the 3D scene); findings stay a count. */
  const word = node.resilience !== undefined ? sealWord(node, state) : null;

  // Upstream dependencies: edges where THIS node is the from side.
  const upstream = edges
    .filter((e) => e.from_id === node.id)
    .map((e) => byId.get(e.to_id))
    .filter((n): n is TwinNode => Boolean(n));

  const blastQuery = useTwinBlast({ kind: node.kind, id: node.id });
  const reached = blastQuery.data?.summary.reached_total ?? 0;
  const annualSar = parseDecimal(blastQuery.data?.summary.annual_revenue_at_risk_sar);

  return (
    <aside
      data-testid="constellation-inspector"
      // P87: compact floating card pinned to the bottom-right of
      // the stage instead of the full-height end-0 overlay. Keeps
      // the graph (the main event) visible behind/around the
      // inspector; max-h + overflow-y-auto handles long deps lists.
      className="absolute bottom-4 end-4 w-72 max-h-[50%] overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 shadow-2xl"
    >
      <button
        type="button"
        data-testid="constellation-inspector-close"
        onClick={onClose}
        className="float-end text-slate-400 hover:text-slate-700"
        aria-label={t("modules.twin.constellation.inspector.close")}
      >
        ×
      </button>
      {/* Round 12 H5 -- the kind word and its qualifier rendered as
          "Important Business Service- MVB": the catalogue string
          carried its own leading separator and the call site supplied
          no space, so the separator arrived glued to the left value.
          One separator, here, where the spacing is decided. */}
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        {/* Round 16 L2 -- a function is headed as one, and the MVB
            marker is an IBS's: a function is never "critical" or MVB
            gold here (P429.6d). */}
        {node.kind === "ibs" && node.service_class === "internal"
          ? t("vocab.entityKind.ibs_service_internal")
          : KIND_LABEL[node.kind]}
        {node.kind === "ibs" && node.mvb_flag && node.service_class !== "internal" ? (
          <>
            {" \u00b7 "}
            {t("screens.inspector.mvb")}
          </>
        ) : null}
        {node.kind === "vendor" && node.material_flag ? (
          <>
            {" \u00b7 "}
            {t("screens.inspector.material")}
          </>
        ) : null}
      </div>
      <h3 className="text-base font-semibold text-slate-900">{node.name}</h3>

      <div className="mt-2 flex flex-wrap gap-2">
        {word !== null ? (
          <button
            type="button"
            data-testid="constellation-inspector-state-pill"
            data-assessed={word === "notAssessed" ? "no" : "yes"}
            data-seal={word}
            onClick={onTraceState}
            title={
              word === "notAssessed"
                ? t("modules.twin.constellation.inspector.notAssessedTitle")
                : word === "notSealed"
                  ? t("modules.twin.constellation.inspector.notSealedTitle")
                  : t("modules.twin.constellation.inspector.stateTrace")
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs hover:border-brand-brass/40 hover:bg-brand-brass/5"
          >
            {SEAL_DOT[word] === null ? (
              <span className="inline-block h-2 w-2 rounded-full border border-slate-400" />
            ) : (
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: SEAL_DOT[word] ?? undefined }}
              />
            )}
            {t(`modules.twin.constellation.inspector.seal.${word}`)}
          </button>
        ) : (
          <button
            type="button"
            data-testid="constellation-inspector-state-pill"
            data-assessed={unassessed ? "no" : "yes"}
            onClick={onTraceState}
            title={
              unassessed
                ? t("modules.twin.constellation.inspector.notAssessedTitle")
                : t("modules.twin.constellation.inspector.stateTrace")
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs hover:border-brand-brass/40 hover:bg-brand-brass/5"
          >
            {unassessed ? (
              <span className="inline-block h-2 w-2 rounded-full border border-slate-400" />
            ) : (
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: STATE_DOT[state] }}
              />
            )}
            {unassessed
              ? t("modules.twin.constellation.inspector.notAssessed")
              : STATE_LABEL[state]}
          </button>
        )}
        {/* Round 19 B30 -- the pill's meaning said on the page, not only
            in a title nobody can reach by touch or by a reader. */}
        {(word === "notAssessed" || (word === null && unassessed)) && (
          <p
            data-testid="constellation-inspector-not-assessed-why"
            className="basis-full text-[11px] text-slate-500"
          >
            {t("modules.twin.constellation.inspector.notAssessedWhy")}
          </p>
        )}
        {node.open_finding_count > 0 && (
          <span
            data-testid="constellation-inspector-findings"
            className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700"
          >
            {node.open_finding_count}{" "}
            {t("modules.twin.constellation.inspector.openFindings")}
          </span>
        )}
      </div>

      <dl className="mt-3 space-y-1 text-xs">
        {/* Round 16 L2 -- revenue at risk is an IBS's measure; the
            methodology states none for a function. */}
        {node.kind === "ibs" &&
          node.service_class !== "internal" &&
          node.annual_revenue_sar && (
            <KV
              label={t("modules.twin.constellation.inspector.revenueAtRisk")}
              value={`SAR ${parseDecimal(node.annual_revenue_sar).toLocaleString()}/yr`}
            />
          )}
        {node.kind === "ibs" && node.sovereign_flag !== null && (
          <KV
            label={t("modules.twin.constellation.inspector.sovereign")}
            value={node.sovereign_flag ? "Yes" : "No"}
          />
        )}
        {node.kind === "ibs" && (
          <KV
            label={t("modules.twin.constellation.inspector.inMvb")}
            value={node.mvb_flag ? "Yes" : "No"}
          />
        )}
        {node.kind === "ibs" && node.service_class === "internal" && (
          <KV
            label={t("modules.twin.constellation.inspector.registerClass")}
            value={t("modules.twin.constellation.inspector.cif")}
          />
        )}
        {node.kind === "ibs" && node.service_class === "state" && (
          <KV
            label={t("modules.twin.constellation.inspector.registerClass")}
            value={t("modules.twin.constellation.inspector.stateService")}
          />
        )}
        {node.kind === "service" && node.biz_crit && (
          <KV
            label={t("modules.twin.constellation.inspector.bizCrit")}
            value={node.biz_crit}
          />
        )}
        {node.kind === "vendor" && node.material_flag !== null && (
          <KV
            label={t("modules.twin.constellation.inspector.materiality")}
            value={node.material_flag ? "Material" : "Non-material"}
          />
        )}
        <KV
          label={t("modules.twin.constellation.inspector.depsCount")}
          value={`${upstream.length}`}
        />
        <KV
          label={t("modules.twin.constellation.inspector.reach")}
          value={blastQuery.isPending ? "…" : `${reached}`}
        />
      </dl>

      {upstream.length > 0 && (
        <>
          <p className="mt-3 text-[10px] uppercase tracking-wide text-slate-500">
            {t("modules.twin.constellation.inspector.dependsOn")}
          </p>
          <ul
            data-testid="constellation-inspector-deps"
            className="space-y-0.5 text-xs"
          >
            {upstream.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  data-testid="constellation-inspector-dep"
                  data-id={u.id}
                  onClick={() => onSelect(u.id)}
                  className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-start hover:bg-slate-100"
                >
                  {/* Round 18 B16 -- a list of dependencies carries no
                      verdict; it drew every one green. */}
                  <span className="inline-block h-2 w-2 rounded-full border border-slate-400" />
                  <span className="truncate">{u.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-3 text-[10px] uppercase tracking-wide text-slate-500">
        {t("modules.twin.constellation.inspector.blastTitle")}
      </p>
      <p className="text-xs text-slate-700">
        {blastQuery.isPending
          ? t("modules.twin.constellation.inspector.blastLoading")
          : reached === 0
            ? t("modules.twin.constellation.inspector.blastNone")
            : t("modules.twin.constellation.inspector.blastSummary", {
                count: reached,
                sar: formatSARCompact(annualSar),
              })}
      </p>
      {/* Round 19 H57 -- a channel offered this and answered "Could not
          load trace · no blast-radius snapshot for channel/<id>": the
          blast radius is sealed for services, vendors and IBSes, never a
          channel. No offer, and the way to one that is sealed. */}
      {node.kind === "channel" ? (
        <p
          data-testid="constellation-inspector-derive-none"
          className="mt-2 text-xs text-slate-500"
        >
          {t("modules.twin.constellation.inspector.deriveNoneChannel")}
        </p>
      ) : (
        <button
          type="button"
          data-testid="constellation-inspector-derive"
          onClick={onTraceBlast}
          className="mt-2 inline-flex items-center gap-1 rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10"
        >
          {t("modules.twin.constellation.inspector.deriveButton")}
        </button>
      )}
    </aside>
  );
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900">{value}</dd>
    </div>
  );
}

function useMemoMap(nodes: TwinNode[]): Map<string, TwinNode> {
  // Cheap to rebuild on each render; the inspector only mounts when
  // a node is selected, so the cost is bounded by the node count.
  const m = new Map<string, TwinNode>();
  for (const n of nodes) m.set(n.id, n);
  return m;
}

function parseDecimal(s: string | null | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

// Re-export so tests can assert on the kind label vocabulary.
export { KIND_LABEL };
export type { TwinNodeKind };
